import { useEffect, useRef } from "react";

/**
 * Real-time booking events over WebSocket.
 *
 *   useLiveEvents((ev) => {
 *     // ev = { type: 'booking.created'|'booking.updated'|'booking.cancelled'|'booking.deleted'|'hello',
 *     //        at: iso, booking: {...} }
 *   });
 *
 * Auto-reconnects with exponential back-off (1s → 15s). Authentication is
 * provided by the same HttpOnly cookie as the REST API.
 */
export function useLiveEvents(handler) {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    let ws = null;
    let closed = false;
    let retry = 1000;
    let heartbeat = null;

    const open = () => {
      const url = backend.replace(/^http/, "ws") + "/api/ws";
      try { ws = new WebSocket(url); } catch { schedule(); return; }
      ws.onopen = () => {
        retry = 1000;
        // Keep the intermediate proxies happy — send a tiny ping every 25s.
        heartbeat = setInterval(() => { try { ws.send("ping"); } catch { /* */ } }, 25000);
      };
      ws.onmessage = (e) => {
        try { handlerRef.current?.(JSON.parse(e.data)); } catch { /* */ }
      };
      ws.onerror = () => { /* let onclose handle reconnect */ };
      ws.onclose = () => {
        if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
        if (!closed) schedule();
      };
    };
    const schedule = () => {
      if (closed) return;
      setTimeout(open, retry);
      retry = Math.min(retry * 2, 15000);
    };

    open();
    return () => {
      closed = true;
      if (heartbeat) clearInterval(heartbeat);
      if (ws) { try { ws.close(); } catch { /* */ } }
    };
  }, []);
}
